import { FeatureClasses } from "../constants/NauticalObjects";
import { LayerFieldsCommon, LayerFieldsLys, LayerFieldsIB, LayerFieldsFastSjomerke } from "../constants/LayerFields";
import { getNFSStatusName, NFSStatus } from "./nfsUtils";
import type Graphic from "@arcgis/core/Graphic";

export type FastPathSksdRule = "never" | "always" | "rekkeviddeRule";

export interface DirectToDefaultFieldSpec {
    field: string;
    layers: FeatureClasses[];
    sksd: FastPathSksdRule;
}

/**
 * Attributes that always change alongside any user edit and must be ignored when computing
 * the "what changed" set for fast-path purposes. `nfs_status` flips to PROJECTED_EXISTING
 * on every attribute save (see FeatureField.tsx); `oppdatert_*` are audit fields written
 * by ArcGIS automatically.
 */
const IGNORED_DIFF_FIELDS = new Set<string>(["nfs_status", "oppdatert_av", "oppdatert_dato"]);

/**
 * Extra fields ignored when diffing the *secondary* feature against its DEFAULT counterpart.
 * These are system-managed attributes that `syncSecondaryFeature` copies from the primary
 * (see SecondaryFeatureCopyAttributeConfig.AllSecondary). We don't want a stray drift on
 * e.g. `sksd_status` to block fast-path for a legitimate `navn` change — on the secondary
 * they are never user-driven edits, only echoes of the primary, which carries the real
 * change and is gated on its own.
 */
export const SECONDARY_ONLY_IGNORED_FIELDS = new Set<string>([
    "sksd_status",
    "kystverk_region",
    "kystverk_rode",
    "pacc",
    "malt_av",
    "malt_dato",
    "nedlagt_av",
    "nedlagt_dato",
    "fjernet_av",
    "fjernet_dato",
]);

const ALL_LAYERS = [
    FeatureClasses.Lys,
    FeatureClasses.IB,
    FeatureClasses.Fastsjømerke,
    FeatureClasses.Flytendemerke,
    FeatureClasses.Racon,
    FeatureClasses.Elektroskap,
    FeatureClasses.Skilt,
];

/**
 * Layers whose features the backend's SKSD transfer endpoint can accept. Mirrors the
 * inline set in exportJobFeaturesWithChangesToSksd (jobsUtils.ts). Used to short-circuit
 * the SKSD step for fast-path-eligible layers that are not actually transferable
 * (e.g. Elektroskap, Skilt) — without this gate, a `sjomerke_nr` edit on Skilt would
 * try to send to SKSD, the backend would reject the unsupported layer, and the user
 * would see a misleading "SKSD failed, retry-jobb opprettet" toast.
 */
const SKSD_TRANSFERABLE_LAYERS = new Set<number>([
    FeatureClasses.Lys,
    FeatureClasses.IB,
    FeatureClasses.Racon,
    FeatureClasses.Fastsjømerke,
    FeatureClasses.Flytendemerke,
    FeatureClasses.Overettlinjelys,
    FeatureClasses.Overettlinjemerke,
]);

export const directToDefaultFields: DirectToDefaultFieldSpec[] = [
    // General editor-screen attributes
    { field: LayerFieldsCommon.navn, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.sjomerke_nr, layers: ALL_LAYERS, sksd: "always" },
    { field: LayerFieldsCommon.beliggenhet, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.sted, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.beskrivelse, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.byggehoyde, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.nautisk_tilstand, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.bygg_tilstand, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.nautisk_funksjon, layers: ALL_LAYERS, sksd: "never" },
    // Backfilling a missing region/rode is a metadata correction with no nautical meaning,
    // so it goes straight to DEFAULT and is never transferred to SKSD.
    { field: LayerFieldsLys.kystverk_region, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsCommon.kystverk_rode, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsLys.kategori, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsLys.navigasjonsinstallasjon_eier, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsLys.type_id, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsFastSjomerke.vanndybde, layers: ALL_LAYERS, sksd: "never" },
    { field: LayerFieldsIB.retningslys_retning, layers: [FeatureClasses.IB, FeatureClasses.Lys], sksd: "never" },
    { field: LayerFieldsIB.karakter, layers: [FeatureClasses.IB], sksd: "never" }, // IB special-case per spec
    { field: LayerFieldsFastSjomerke.toppmerke_type, layers: [FeatureClasses.Fastsjømerke], sksd: "never" },
    { field: LayerFieldsFastSjomerke.toppmerke_retning, layers: [FeatureClasses.Fastsjømerke], sksd: "never" },
    { field: LayerFieldsFastSjomerke.refleks_farge, layers: [FeatureClasses.Fastsjømerke], sksd: "never" },
    {
        field: LayerFieldsFastSjomerke.merke_type,
        layers: [FeatureClasses.Fastsjømerke, FeatureClasses.Flytendemerke],
        sksd: "never",
    },
    {
        field: LayerFieldsFastSjomerke.radar_reflektor,
        layers: [FeatureClasses.Fastsjømerke, FeatureClasses.Flytendemerke],
        sksd: "never",
    },

    // Light-calc-owned fields. These arrive on the editor as pending changes
    // from a prior LightCalculator save and must be recognized here.
    { field: LayerFieldsLys.lampe, layers: [FeatureClasses.Lys], sksd: "never" },
    { field: LayerFieldsLys.karakter, layers: [FeatureClasses.Lys], sksd: "always" },
    { field: LayerFieldsLys.lys_karakter, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "always" }, // "fyrlistekarakter"
    { field: LayerFieldsLys.lys_farge, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "always" },
    { field: LayerFieldsLys.periode, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "always" },
    { field: LayerFieldsLys.bakgrunnsbelysning, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.lys_gjennomgang, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.lysstyrke_hvit, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.lysstyrke_rod, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.lysstyrke_gronn, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.lysstyrke_gul, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.rekkevidde_hvit, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "rekkeviddeRule" },
    { field: LayerFieldsLys.rekkevidde_rod, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "rekkeviddeRule" },
    { field: LayerFieldsLys.rekkevidde_gronn, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "rekkeviddeRule" },
    { field: LayerFieldsLys.rekkevidde_gul, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "rekkeviddeRule" },
    { field: LayerFieldsLys.lys_hoyde, layers: [FeatureClasses.Lys, FeatureClasses.IB], sksd: "never" },
    { field: LayerFieldsLys.linse_orden, layers: [FeatureClasses.Lys], sksd: "never" },
];

/** Sector layers related to a light. A change to any of these blocks the fast path. */
export const SECTOR_LAYER_IDS: number[] = [
    FeatureClasses.Sektorlinje, // 2
    FeatureClasses.Sektorflate, // 3
    FeatureClasses.Lyssektor, // 19
];

/**
 * Reduces the version-differences list to a `layerId -> Set<objectId>` map for the sector
 * layers only. inserts/updates/deletes are merged (any presence is a "change"). ObjectIds
 * are normalized to strings. Layers with no changes are omitted. Takes a structural type so
 * this module stays decoupled from newVersioningUtils' DifferencesObjectIDObject.
 */
export function buildChangedSectorMap(
    differences: { layerId: number; inserts?: string[]; updates?: string[]; deletes?: string[] }[]
): Map<number, Set<string>> {
    const map = new Map<number, Set<string>>();
    for (const diff of differences) {
        if (!SECTOR_LAYER_IDS.includes(diff.layerId)) continue;
        const ids = map.get(diff.layerId) ?? new Set<string>();
        for (const id of [...(diff.inserts ?? []), ...(diff.updates ?? []), ...(diff.deletes ?? [])]) {
            ids.add(String(id));
        }
        if (ids.size > 0) map.set(diff.layerId, ids);
    }
    return map;
}

/**
 * True when any of the light's own sector objectIds (per layer) appears in the version's
 * changed-sector set. Pure set intersection — no attribute or geometry comparison.
 */
export function lightHasChangedSectors(
    changedByLayer: Map<number, Set<string>>,
    lightSectorByLayer: Map<number, Set<string>>
): boolean {
    for (const layerId of SECTOR_LAYER_IDS) {
        const changed = changedByLayer.get(layerId);
        const owned = lightSectorByLayer.get(layerId);
        if (!changed || !owned) continue;
        for (const id of owned) {
            if (changed.has(id)) return true;
        }
    }
    return false;
}

const fieldSpecsForLayer = (layerId: number): Map<string, DirectToDefaultFieldSpec> => {
    const map = new Map<string, DirectToDefaultFieldSpec>();
    for (const spec of directToDefaultFields) {
        if (spec.layers.includes(layerId)) {
            map.set(spec.field, spec);
        }
    }
    return map;
};

export function isFastPathAllowed(
    layerId: number,
    versionStatus: number,
    changedFields: string[],
    geometryChanged: boolean
): boolean {
    if (changedFields.length === 0) return false;
    const changes: FieldChange[] = changedFields.map((field) => ({ field, oldValue: undefined, newValue: undefined }));
    const reasons = fastPathSideReasons(
        { layerId, versionStatus, defaultStatus: NFSStatus.OPERATIVE, changes, geometryChanged },
        "primary"
    );
    return reasons.length === 0;
}

export interface FieldChange {
    field: string;
    oldValue: unknown;
    newValue: unknown;
}

const rekkeviddeRequiresSksd = (oldValue: unknown, newValue: unknown): boolean => {
    const oldIsNull = oldValue === null || oldValue === undefined;
    const newIsNull = newValue === null || newValue === undefined;

    // null ↔ value transitions (either direction) trigger SKSD
    if (oldIsNull !== newIsNull) return true;
    if (oldIsNull && newIsNull) return false;

    const oldNum = Number(oldValue);
    const newNum = Number(newValue);
    if (Number.isNaN(oldNum) || Number.isNaN(newNum)) return false;

    if (newNum < oldNum) return true;
    if (newNum - oldNum >= 1) return true;
    return false;
};

export function fastPathRequiresSksd(layerId: number, changes: FieldChange[]): boolean {
    if (!SKSD_TRANSFERABLE_LAYERS.has(layerId)) return false;
    const specsByField = fieldSpecsForLayer(layerId);
    for (const change of changes) {
        const spec = specsByField.get(change.field);
        if (!spec) continue; // not in whitelist — shouldn't happen if isFastPathAllowed gated earlier
        if (spec.sksd === "always") return true;
        if (spec.sksd === "rekkeviddeRule" && rekkeviddeRequiresSksd(change.oldValue, change.newValue)) {
            return true;
        }
    }
    return false;
}

export type FastPathSide = "primary" | "secondary";

export type FastPathBlockReason =
    | { code: "status"; side: FastPathSide; status: number }
    | { code: "geometry"; side: FastPathSide }
    | { code: "not-whitelisted"; side: FastPathSide; fields: string[] }
    | { code: "missing-default-status"; side: FastPathSide }
    | { code: "sksd-not-operative"; defaultStatus: number }
    | { code: "partner-missing" }
    | { code: "related-sector-changes" };

export interface FastPathSideInput {
    layerId: number;
    versionStatus: number;
    /** The object's nfs_status in planroot (DEFAULT). */
    defaultStatus: number | null | undefined;
    changes: FieldChange[];
    geometryChanged: boolean;
}

export interface FastPathEvaluation {
    canPost: boolean;
    /**
     * True only when SKSD is required AND every involved side is OPERATIVE in planroot (or
     * PROJECTED_EXISTING when `allowSksdOnProjectedExisting` is on).
     */
    sksdWillSend: boolean;
    /** True when the post promotes at least one side from PROJECTED_EXISTING to OPERATIVE. */
    promotesToOperative: boolean;
    /** Empty when canPost is true. */
    reasons: FastPathBlockReason[];
    /**
     * Per-side restore target = that side's planroot status, or OPERATIVE for every side
     * when the changeset is sent to SKSD.
     */
    perSideRestore: { primary: number | null; secondary?: number | null };
}

/**
 * Primary layers whose PROJECTED_EXISTING objects may be promoted to OPERATIVE when the
 * changeset requires SKSD. A Lysbøye is a Lys primary, so its Flytende merke secondary is
 * covered; standalone merker and other non-lights are not.
 */
const SKSD_PROMOTION_PRIMARY_LAYERS = new Set<number>([FeatureClasses.Lys, FeatureClasses.IB]);

const SKSD_PROMOTABLE_DEFAULT_STATUSES = new Set<number>([NFSStatus.OPERATIVE, NFSStatus.PROJECTED_EXISTING]);

/**
 * Both the planroot AND the version status are checked per side: an untouched secondary
 * skips the own-side status gate (and `nfs_status` is not part of the diff), so without the
 * version check a side with an unexpected version status would be overwritten to OPERATIVE.
 */
const isSidePromotable = (side: FastPathSideInput): boolean =>
    SKSD_PROMOTABLE_DEFAULT_STATUSES.has(side.defaultStatus as number) &&
    SKSD_PROMOTABLE_DEFAULT_STATUSES.has(side.versionStatus);

const isPromotableToOperative = (primary: FastPathSideInput, secondary?: FastPathSideInput | null): boolean =>
    SKSD_PROMOTION_PRIMARY_LAYERS.has(primary.layerId) &&
    isSidePromotable(primary) &&
    (!secondary || isSidePromotable(secondary));

const sksdBlockReason = (
    primary: FastPathSideInput,
    secondary: FastPathSideInput | null | undefined,
    partnerMissing: boolean
): FastPathBlockReason => {
    if (partnerMissing) return { code: "partner-missing" };
    const offending =
        secondary && secondary.defaultStatus !== NFSStatus.OPERATIVE ? secondary.defaultStatus : primary.defaultStatus;
    return { code: "sksd-not-operative", defaultStatus: (offending as number) ?? NFSStatus.UNDEFINED };
};

/** SKSD only accepts operative objects, so a sent changeset leaves every side OPERATIVE. */
const restoreTargets = (
    primary: FastPathSideInput,
    secondary: FastPathSideInput | null | undefined,
    sksdWillSend: boolean
): FastPathEvaluation["perSideRestore"] => {
    const target = (side: FastPathSideInput): number | null =>
        sksdWillSend ? NFSStatus.OPERATIVE : (side.defaultStatus ?? null);
    return {
        primary: target(primary),
        ...(secondary ? { secondary: target(secondary) } : {}),
    };
};

/**
 * Own-side gates only: geometry (always), status + whitelist (only when the side carries
 * changes). The default-status presence check and the pair-level SKSD/operative rule live
 * in `evaluateFastPath`.
 */
const fastPathSideReasons = (input: FastPathSideInput, side: FastPathSide): FastPathBlockReason[] => {
    const reasons: FastPathBlockReason[] = [];
    if (input.geometryChanged) {
        reasons.push({ code: "geometry", side });
    }
    if (input.changes.length > 0) {
        if (input.versionStatus !== NFSStatus.PROJECTED_EXISTING) {
            reasons.push({ code: "status", side, status: input.versionStatus });
        }
        const specsByField = fieldSpecsForLayer(input.layerId);
        const nonWhitelisted = input.changes.map((c) => c.field).filter((f) => !specsByField.has(f));
        if (nonWhitelisted.length > 0) {
            reasons.push({ code: "not-whitelisted", side, fields: nonWhitelisted });
        }
    }
    return reasons;
};

const isMissingStatus = (status: number | null | undefined): boolean =>
    status === null || status === undefined || status === NFSStatus.UNDEFINED;

/**
 * The single source of truth for the fast-path gate, the block reasons, and the per-side
 * restore targets. Called by both the UI button gate and the orchestrator re-gate.
 *
 * Rules:
 *  - Each side's own gate (status / geometry / whitelist) must pass.
 *  - Every involved side must have a known planroot status (needed for a safe restore).
 *  - `restoreStatus` per side = that side's planroot status.
 *  - SKSD is a single, pair-level send routed via the primary. A pair is functionally one
 *    joined object, so SKSD is permitted only when EVERY involved side is OPERATIVE in
 *    planroot; otherwise an SKSD-requiring changeset is blocked.
 *  - With `allowSksdOnProjectedExisting` (feature toggle), a Lys/IB primary whose sides are
 *    each OPERATIVE or PROJECTED_EXISTING in planroot may also send to SKSD. Every side is
 *    then restored to OPERATIVE, since SKSD only accepts operative nautical objects. Not
 *    when `partnerMissing`: an incomplete pair would end up with mixed statuses.
 */
export function evaluateFastPath(
    primary: FastPathSideInput,
    secondary?: FastPathSideInput | null,
    options?: { relatedSectorChanged?: boolean; allowSksdOnProjectedExisting?: boolean; partnerMissing?: boolean }
): FastPathEvaluation {
    const reasons: FastPathBlockReason[] = [];

    reasons.push(...fastPathSideReasons(primary, "primary"));
    if (secondary) {
        reasons.push(...fastPathSideReasons(secondary, "secondary"));
    }

    if (options?.relatedSectorChanged) {
        reasons.push({ code: "related-sector-changes" });
    }

    if (isMissingStatus(primary.defaultStatus)) {
        reasons.push({ code: "missing-default-status", side: "primary" });
    }
    if (secondary && isMissingStatus(secondary.defaultStatus)) {
        reasons.push({ code: "missing-default-status", side: "secondary" });
    }

    const primaryRequiresSksd = fastPathRequiresSksd(primary.layerId, primary.changes);
    const secondaryRequiresSksd = secondary ? fastPathRequiresSksd(secondary.layerId, secondary.changes) : false;
    const anySksdRequired = primaryRequiresSksd || secondaryRequiresSksd;
    const allDefaultOperative =
        primary.defaultStatus === NFSStatus.OPERATIVE &&
        (secondary ? secondary.defaultStatus === NFSStatus.OPERATIVE : true);
    const anyDefaultMissing =
        isMissingStatus(primary.defaultStatus) || (secondary ? isMissingStatus(secondary.defaultStatus) : false);
    const promotionAllowed = options?.allowSksdOnProjectedExisting === true;
    const canPromoteToOperative =
        promotionAllowed && options?.partnerMissing !== true && isPromotableToOperative(primary, secondary);
    if (anySksdRequired && !allDefaultOperative && !anyDefaultMissing && !canPromoteToOperative) {
        reasons.push(sksdBlockReason(primary, secondary, promotionAllowed && options?.partnerMissing === true));
    }

    const hasAnyChanges = primary.changes.length > 0 || (secondary ? secondary.changes.length > 0 : false);
    const canPost = hasAnyChanges && reasons.length === 0;
    const sksdWillSend = canPost && anySksdRequired;
    const promotesToOperative = sksdWillSend && !allDefaultOperative;

    return {
        canPost,
        sksdWillSend,
        promotesToOperative,
        reasons,
        perSideRestore: restoreTargets(primary, secondary, sksdWillSend),
    };
}

/** Norwegian, user-facing text for a block reason (info popover + orchestrator abort toast). */
export function fastPathReasonText(reason: FastPathBlockReason): string {
    const sideLabel = (side: FastPathSide): string => (side === "primary" ? "Hovedobjektet" : "Sekundærobjektet");
    switch (reason.code) {
        case "status":
            return `${sideLabel(reason.side)} har ikke status «Prosjektert/Eksisterende» (status ${getNFSStatusName(reason.status)}).`;
        case "geometry":
            return `${sideLabel(reason.side)} har endret geometri – direkte posting er ikke tillatt.`;
        case "not-whitelisted":
            return `${sideLabel(reason.side)} har endringer i felt som ikke kan postes direkte: ${reason.fields.join(
                ", "
            )}.`;
        case "missing-default-status":
            return `${sideLabel(reason.side)} mangler status i planroot – direkte posting er ikke tillatt.`;
        case "sksd-not-operative":
            return `Endringen må sendes til SKSD, men objektet er ikke operativt i planroot (status ${getNFSStatusName(reason.defaultStatus)}). Direkte posting er ikke tillatt.`;
        case "partner-missing":
            return "Fant ikke det tilknyttede objektet (HIB/IB, FL/LA/IB eller lysbøye/flytende merke). Endringen må sendes til SKSD og kan ikke postes direkte for bare den ene delen.";
        case "related-sector-changes":
            return "Lyset har endringer i tilknyttede sektorer (lyssektor/sektorlinje/sektorflate). Disse kan ikke postes direkte – bruk vanlig jobb-flyt.";
    }
}

/**
 * Compares the version graphic against the DEFAULT graphic and returns the list of attributes
 * that differ. `null` and `undefined` are treated as equivalent.
 *
 * When diffing a secondary feature, callers should pass `SECONDARY_ONLY_IGNORED_FIELDS` as
 * `extraIgnoredFields` so sync-managed attributes (sksd_status, malt_*, etc.) don't show up
 * as user-driven changes.
 */
export function computeChangedFieldsVsDefault(
    versionGraphic: Graphic,
    defaultGraphic: Graphic | null,
    extraIgnoredFields?: ReadonlySet<string>
): FieldChange[] {
    if (!defaultGraphic) return [];
    const changes: FieldChange[] = [];
    const seen = new Set<string>();
    const isIgnored = (field: string): boolean =>
        IGNORED_DIFF_FIELDS.has(field) || (extraIgnoredFields?.has(field) ?? false);

    for (const field of Object.keys(versionGraphic.attributes)) {
        seen.add(field);
        if (isIgnored(field)) continue;
        const oldValue = defaultGraphic.attributes[field];
        const newValue = versionGraphic.attributes[field];
        if (valuesDiffer(oldValue, newValue)) {
            changes.push({ field, oldValue, newValue });
        }
    }
    // Pick up fields that exist only on the default graphic (deleted by client?)
    for (const field of Object.keys(defaultGraphic.attributes)) {
        if (seen.has(field)) continue;
        if (isIgnored(field)) continue;
        const oldValue = defaultGraphic.attributes[field];
        const newValue = versionGraphic.attributes[field];
        if (valuesDiffer(oldValue, newValue)) {
            changes.push({ field, oldValue, newValue });
        }
    }
    return changes;
}

const valuesDiffer = (a: unknown, b: unknown): boolean => {
    const aIsNull = a === null || a === undefined;
    const bIsNull = b === null || b === undefined;
    if (aIsNull && bIsNull) return false;
    if (aIsNull !== bIsNull) return true;
    return a !== b;
};
